import { BreakpointObserver } from '@angular/cdk/layout';
import { ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core';
import { IFilterResponse, ILink } from '@impact/data';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import SwiperCore, { Navigation, Scrollbar } from 'swiper/core';
import { LazyOptions } from 'swiper/types';

import { FilterService } from '../filter-service/filter.service';
import { FeatureDetectionService } from '../utils/helpers/feature-detection.service';
import { FILTER_FACET, FILTER_SORTING } from '../filter-service/filter.models';

// import Swiper core and required modules
// install Swiper modules
SwiperCore.use([Navigation, Scrollbar]);

enum LayoutView {
    GRID = 'Grid',
    SLIDER = 'Slider'
}

@Component({
    selector: 'app-used-cars-list',
    template: `
    <div class="used-cars-list" appInViewport (intersectOutput)="intersectInitComponent()">

        <ng-container [ngSwitch]="sliderDataReady">

            <!-- Loading skeleton -->
            <div class="loading-skeleton" *ngSwitchCase="false">
                <div class="product-skeleton-item" *ngFor="let item of skeletonArray">
                    <div class="product-skeleton-item__text"></div>
                    <div class="product-skeleton-item__bottom"></div>
                </div>
            </div>

            <ng-container *ngSwitchCase="true">
                <ng-container [ngSwitch]="componentLayout">
                    <!-- SLIDER layout -->
                    <ng-container *ngSwitchCase="layoutViewEnum.SLIDER">
                        <!-- https://swiperjs.com/angular -->
                        <swiper
                            *ngIf="isBrowser"
                            class="used-cars-list__swiper"
                            [slidesPerView]="slidesAmount"
                            [slidesPerGroup]="slidesGroupAmount"
                            [spaceBetween]="slidesSpace"
                            [navigation]="showNavigation"
                            [scrollbar]="{ draggable: true }">
                            <ng-template swiperSlide *ngFor="let car of itemsToShow">
                                <app-product-item
                                    class="used-cars-list__item"
                                    [sliderImageSize]="imageSize"
                                    [sliderImagesAmount]="3"
                                    [product]="car">
                                </app-product-item>
                            </ng-template>

                            <ng-template swiperSlide class="used-cars-list__item" *ngIf="showLastSlide">
                                <div class="used-cars-list__item--content">
                                    <h6 *ngIf="lastSlideText">{{lastSlideText}}</h6>
                                    <a *ngIf="lastSlideLink?.url"
                                        [routerLink]="lastSlideLink.url"
                                        [queryParams]="lastSlideLink.urlParams"
                                        class="button--primary button--themed">
                                        {{lastSlideLink.name}}
                                    </a>
                                </div>
                            </ng-template>
                        </swiper>
                    </ng-container>

                    <!-- GRID layout -->
                    <div class="used-cars-list__grid" *ngSwitchCase="layoutViewEnum.GRID">
                        <div class="used-cars-list">
                            <app-product-item *ngFor="let car of itemsToShow"
                                class="used-cars-list__item"
                                [sliderImageSize]="imageSize"
                                [product]="car">
                            </app-product-item>
                        </div>

                        <div class="used-cars-list__grid-actions" *ngIf="showLastSlide">
                            <h6 *ngIf="lastSlideText">{{lastSlideText}}</h6>
                            <a *ngIf="lastSlideLink?.url"
                                [routerLink]="lastSlideLink.url"
                                [queryParams]="lastSlideLink.urlParams"
                                class="button--primary button--themed">
                                {{lastSlideLink.name}}
                            </a>
                        </div>
                    </div>
                </ng-container>
            </ng-container>
        </ng-container>
    </div>
    `
})

export class UsedCarsListComponent implements OnDestroy {
    @Input() query: string;
    @Input() lastSlideText: string;
    @Input() lastSlideLink: ILink;
    @Input() displayAmount: number;
    @Input() layoutMode?: string;
    @Input() isUsedCarRelated?: boolean;
    @Input() currentVehicleId?: number | undefined;

    carsData: IFilterResponse;
    slidesAmount: number;
    slidesGroupAmount: number;
    slidesSpace: number;
    imageSize: number;
    showNavigation: boolean;
    sliderDataReady = false;
    layoutViewEnum = LayoutView;
    componentLayout: string;
    itemsToShow: any[] = [];
    showLastSlide = false;
    isBrowser: boolean;
    make: string;
    skeletonArray: any[] = [];

    lazyOptions: LazyOptions = {
        loadOnTransitionStart: false,
        loadPrevNext: true
    };

    private readonly unsubscribe = new Subject<void>();

    constructor(
        private breakpointObserver: BreakpointObserver,
        private filterService: FilterService,
        private cdr: ChangeDetectorRef,
        featureDetectionService: FeatureDetectionService
    ) {
        this.isBrowser = featureDetectionService.isBrowser();
        this.skeletonArray = new Array(6);
    }

    // Will init when intersection observer directive triggers it
    intersectInitComponent() {
        // Set layout mode
        this.componentLayout = this.layoutMode ? this.layoutMode : LayoutView.GRID;

        if (this.lastSlideText || this.lastSlideLink) {
            this.showLastSlide = true;
        }

        if (!Number(this.displayAmount)) {
            this.displayAmount = 15;
        }

        if (this.query) {
            // Get used cars query
            this.filterService.independentFetchFilter(this.query, this.displayAmount)
                .pipe(takeUntil(this.unsubscribe))
                .subscribe((response: IFilterResponse) => {
                    this.carsData = response;

                    if (this.carsData && this.carsData.total && this.carsData.hits) {

                        // Check if we need to get more data
                        if (this.isUsedCarRelated && this.displayAmount) {
                            this.make = this.query.split('&')[0].split('=')[1];

                            // If there is not enough data based on make and model, we get more data based on make
                            if (this.carsData?.total === 0 || this.carsData?.total < this.displayAmount) {
                                this.itemsToShow = this.removeCurrentVehicleFromResults(this.carsData.hits.slice(0, this.displayAmount));
                                this.getVehicleMakeData(this.make, this.displayAmount - this.itemsToShow.length + 1);
                            } else {
                                this.itemsToShow = this.removeCurrentVehicleFromResults(this.carsData.hits.slice(0, this.displayAmount));
                                this.cdr.markForCheck();
                                this.initSlider();
                            }
                        } else {
                            this.itemsToShow = this.carsData.hits.slice(0, this.displayAmount);
                            this.cdr.markForCheck();
                            this.initSlider();
                        }
                    }
                });
        } else {
            // if for some reason we don't have a specific query we should get random data
            this.getVehicleRandomData(this.displayAmount);
        }
    }

    // dataType = make, or random
    getVehicleMakeData(make: string, amount: number) {
        this.filterService.independentFetchFilter(`${FILTER_FACET.MAKE}=${make}&sort=${FILTER_SORTING.MODEL_ASCENDING}`, amount)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((response: IFilterResponse) => {
                if (response.hits && this.carsData.hits) {
                    this.itemsToShow = this.itemsToShow.concat(this.removeCurrentVehicleFromResults(response.hits));

                    // Check if we need to get more data
                    if (response.hits.length < amount && this.displayAmount) {
                        this.getVehicleRandomData(this.displayAmount - this.itemsToShow.length + 1);
                    } else {
                        this.cdr.markForCheck();
                        this.initSlider();
                    }
                }
            });
    }

    // Get more data (random vehicles) to fill
    getVehicleRandomData(amount: number) {
        this.filterService.independentFetchFilter('', amount)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((response: IFilterResponse) => {
                if (response?.hits) {
                    this.itemsToShow = (this.itemsToShow ?? []).concat(this.removeCurrentVehicleFromResults(response.hits));
                    this.cdr.markForCheck();
                    this.initSlider();
                }
            });
    }

    // Remove any current vehicle from results, if is used car related
    removeCurrentVehicleFromResults(vehicles: any): any[] {
        if (this.currentVehicleId) {
            return vehicles.filter((item: any) => {
                return item.id !== this.currentVehicleId;
            });
        } else {
            return vehicles;
        }
    }

    initSlider() {
        // Detect layout changes
        const queries$ = this.breakpointObserver.observe([
            '(min-width: 460px)',
            '(min-width: 768px)',
            '(min-width: 1024px)',
            '(min-width: 1200px)',
            '(min-width: 1580px)'
        ]);

        queries$
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((result) => {

                if (!result.matches) {
                    this.setSlidesAmount(1);
                    this.slidesSpace = 14;
                    this.imageSize = 300;
                } else {
                    if (result.breakpoints['(min-width: 1580px)'] === true) {
                        this.setSlidesAmount(4);
                        this.slidesSpace = 16;
                        this.imageSize = 300;
                    } else if (result.breakpoints['(min-width: 1200px)'] === true) {
                        this.setSlidesAmount(4);
                        this.slidesSpace = 14;
                        this.imageSize = 300;
                    } else if (result.breakpoints['(min-width: 1024px)'] === true) {
                        this.setSlidesAmount(3);
                        this.slidesSpace = 14;
                        this.imageSize = 300;
                    } else if (result.breakpoints['(min-width: 768px)'] === true) {
                        this.setSlidesAmount(3);
                        this.slidesSpace = 14;
                        this.imageSize = 300;
                    } else if (result.breakpoints['(min-width: 460px)'] === true) {
                        this.setSlidesAmount(2);
                        this.slidesSpace = 14;
                        this.imageSize = 400;
                    }
                }

                this.sliderDataReady = true;
                this.cdr.detectChanges();
            });
    }

    setSlidesAmount(amount: number) {
        if (!this.itemsToShow) {
            console.error('[BN warning: No car data]');
        }

        if (this.itemsToShow && this.itemsToShow.length > amount) {
            this.slidesAmount = amount + .1;
        } else {
            this.slidesAmount = amount;
        }

        if (this.itemsToShow.length + 1 > amount) {
            this.showNavigation = true;
        } else {
            this.showNavigation = false;
        }

        this.slidesGroupAmount = amount;
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
